import { logger } from "@/lib/logger";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart3, TrendingUp, Zap, DollarSign, Play, Mail, Lock, User, Calendar, ExternalLink, ArrowRight, Radar, ArrowUpRight } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { AuditTeaser } from "@/components/AuditTeaser";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MEETING_LINK = "https://calendar.app.google/gbZSzz4TiFAYYSjL8";

export default function LoginPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isDemoLoading, setIsDemoLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showDemoAfterLogin, setShowDemoAfterLogin] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  
  // Form mode: 'signin' or 'signup'
  const [mode, setMode] = useState("signin");
  
  // Form fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: "include",
        });
        if (response.ok) {
          const userData = await response.json();
          if (userData.registered === "Y") {
            navigate("/campaigns", { replace: true });
          } else {
            setPendingUser(userData);
            setShowDemoAfterLogin(true);
          }
        }
      } catch (error) {
        logger.error("Auth check failed (expected if not logged in):", error);
      } finally {
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    setIsLoading(true);
    const origin = window.location.origin;
    window.location.href = `${API}/auth/google/login?origin=${encodeURIComponent(origin)}&next=${encodeURIComponent("/campaigns")}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    
    setIsEmailLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      
      if (response.ok) {
        const userData = await response.json();
        if (userData.token) localStorage.setItem("roas_session_token", userData.token);
        if (userData.registered === "Y") {
          navigate("/campaigns", { state: { user: userData }, replace: true });
        } else {
          setPendingUser(userData);
          setShowDemoAfterLogin(true);
          toast.info("Your account is pending approval. You can explore the demo in the meantime.");
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Login failed");
      }
    } catch (error) {
      toast.error("Login failed");
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    
    if (!email || !password || !name) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    setIsEmailLoading(true);
    try {
      const response = await fetch(`${API}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password, name }),
      });
      
      if (response.ok) {
        const userData = await response.json();
        setPendingUser(userData);
        setShowDemoAfterLogin(true);
        toast.success("Account created! Your account is pending admin approval.");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Signup failed");
      }
    } catch (error) {
      toast.error("Signup failed");
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleQuickDemo = async () => {
    setIsDemoLoading(true);
    try {
      // Get the logged-in user's real email from current session
      let realEmail = null;
      let realName = null;
      const token = localStorage.getItem("roas_session_token");
      const headers = token ? { "Authorization": `Bearer ${token}` } : {};
      try {
        const meResp = await fetch(`${API}/auth/me`, { credentials: "include", headers });
        if (meResp.ok) {
          const me = await meResp.json();
          realEmail = me.email;
          realName = me.name;
        }
      } catch (e) { logger.error("Error:", e); }

      const resp = await fetch(`${API}/auth/demo-session`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(realEmail ? { email: realEmail, name: realName } : {}),
      });
      if (!resp.ok) throw new Error("Failed");
      const data = await resp.json();
      localStorage.setItem("roas_session_token", data.token);
      setTimeout(() => { window.location.href = "/campaigns"; }, 100);
    } catch (e) {
      setIsDemoLoading(false);
      toast.error("Failed to start demo. Please try again.");
    }
  };

  const handleBookMeeting = () => {
    window.open(MEETING_LINK, "_blank");
  };

  const toggleMode = () => {
    setMode(mode === "signin" ? "signup" : "signin");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setName("");
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  // Show pending approval screen for unregistered users
  if (showDemoAfterLogin && pendingUser) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md bg-[#121212] border border-white/10 rounded-2xl p-8"
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
              <BarChart3 className="w-8 h-8 text-amber-400" />
            </div>
            <h2 className="font-heading text-2xl text-white font-bold mb-2">Account Pending Approval</h2>
            <p className="text-zinc-400 text-sm">
              Hi <span className="text-white">{pendingUser.name || pendingUser.email}</span>! Your account is awaiting admin approval.
            </p>
          </div>

          <div className="space-y-3">
            <Button
              data-testid="pending-demo-btn"
              onClick={handleQuickDemo}
              disabled={isDemoLoading}
              className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold rounded-lg py-5"
            >
              {isDemoLoading ? (
                <div className="spinner border-white border-t-transparent" />
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2 fill-current" />
                  Explore Demo
                </>
              )}
            </Button>

            <Button
              onClick={handleBookMeeting}
              variant="outline"
              className="w-full border-white/20 text-white hover:bg-white/10"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Contact Admin
              <ExternalLink className="w-3 h-3 ml-2" />
            </Button>
          </div>

          <p className="text-zinc-500 text-xs text-center mt-6">
            You'll get full access once an admin approves your account
          </p>
        </motion.div>
      </div>
    );
  }

  const features = [
    { icon: BarChart3, title: "Agentic AI Simulator", desc: "Predict ROAS, audience reaction & budget outcomes before you spend a dollar" },
    { icon: TrendingUp, title: "Real-time ROAS", desc: "Track return on ad spend across all platforms" },
    { icon: Zap, title: "AI Optimization", desc: "Automatically optimize budgets for best results" },
    { icon: DollarSign, title: "Cost Control", desc: "Set limits and get alerts before overspending" },
  ];

  return (
    <div className="relative min-h-screen bg-[#0A0A0A] flex flex-col lg:flex-row">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex w-1/2 flex-col justify-center px-10 py-10 xl:px-14 bg-gradient-to-br from-[#0A0A0A] via-[#121212] to-[#0A0A0A]">
        <div className="w-full max-w-xl mx-auto">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-8"
        >
          <div className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center">
            <img src="/roas-ai-logo.png" alt="ROAS-AI" className="w-12 h-12 object-contain" />
          </div>
          <span className="font-heading text-2xl text-white font-bold">ROAS-AI</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-4xl xl:text-5xl font-heading font-bold text-white mb-4 leading-tight"
        >
          Maximize Your
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">
            Ad Performance
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-zinc-400 text-lg mb-6"
        >
          Meet the Agentic AI Simulator — predict a campaign's ROAS, audience reaction and budget scenarios before you spend a dollar. Then connect Google, Meta, LinkedIn & Bing Ads in one dashboard and let AI auto-optimize every budget.
        </motion.p>

        {/* Metrics Uplift Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="flex gap-4 mb-6"
        >
          <div className="flex-1 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-emerald-400">40%</p>
            <p className="text-white text-sm font-semibold">Increase in ROAS</p>
          </div>
          <div className="flex-1 bg-violet-500/10 border border-violet-500/30 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-violet-400">35%</p>
            <p className="text-white text-sm font-semibold">Reduction in CAC</p>
          </div>
        </motion.div>

        {/* Wasted Spend Audit Teaser */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="w-full mb-6"
        >
          <AuditTeaser compact />
        </motion.div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-4 mb-8">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
              className="flex items-start gap-3"
            >
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                <feature.icon className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">{feature.title}</h3>
                <p className="text-zinc-400 text-sm">{feature.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Widget - moved to right panel */}

        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-zinc-500 text-sm"
        >
          Trusted by 50+ marketing teams worldwide
        </motion.p>

        {/* Affiliate Program Widget - moved to right panel */}
        </div>
      </div>

      {/* Right Panel - Auth Forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#0A0A0A] lg:bg-[#121212]">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-6 justify-center">
            <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center">
              <img src="/roas-ai-logo.png" alt="ROAS-AI" className="w-10 h-10 object-contain" />
            </div>
            <span className="font-heading text-xl text-white font-bold">ROAS-AI</span>
          </div>

          {/* Mobile Value Props */}
          <div className="lg:hidden mb-6 space-y-4">
            <h2 className="text-2xl font-heading font-bold text-white text-center leading-tight">
              Maximize Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">Ad Performance</span>
            </h2>
            <div className="flex gap-3 justify-center">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-2.5 text-center">
                <p className="text-2xl font-bold text-emerald-400">40%</p>
                <p className="text-white text-xs font-semibold">Increase in ROAS</p>
              </div>
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl px-4 py-2.5 text-center">
                <p className="text-2xl font-bold text-violet-400">35%</p>
                <p className="text-white text-xs font-semibold">Reduction in CAC</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {features.map((feature) => (
                <div key={feature.title} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
                  <feature.icon className="w-4 h-4 text-violet-400 flex-shrink-0" />
                  <span className="text-white text-xs font-medium">{feature.title}</span>
                </div>
              ))}
            </div>
            <AuditTeaser compact />
          </div>

          <div className="bg-[#121212] lg:bg-[#18181B] border border-white/10 rounded-2xl p-8 lg:p-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={mode}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <div className="text-center mb-6">
                  <h2 className="font-heading text-2xl text-white font-bold mb-2">
                    {mode === "signin" ? "Welcome Back" : "Create Account"}
                  </h2>
                  <p className="text-zinc-400">
                    {mode === "signin" 
                      ? "Sign in to access your dashboard" 
                      : "Sign up to get started with ROAS-AI"}
                  </p>
                </div>

                {/* Email/Password Form */}
                <form onSubmit={mode === "signin" ? handleEmailLogin : handleEmailSignup} className="space-y-4 mb-6">
                  {mode === "signup" && (
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-zinc-400">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input
                          id="name"
                          type="text"
                          placeholder="John Doe"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                          data-testid="signup-name-input"
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-zinc-400">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                        data-testid="auth-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="password" className="text-zinc-400">Password</Label>
                      {mode === "signin" && (
                        <button
                          type="button"
                          onClick={() => navigate("/forgot-password")}
                          className="text-xs text-violet-400 hover:text-violet-300 transition-colors"
                        >
                          Forgot password?
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                        data-testid="auth-password-input"
                      />
                    </div>
                    {mode === "signup" && (
                      <p className="text-zinc-500 text-xs">Minimum 6 characters</p>
                    )}
                  </div>

                  {mode === "signup" && (
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-zinc-400">Confirm Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input
                          id="confirmPassword"
                          type="password"
                          placeholder="••••••••"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                          data-testid="signup-confirm-password-input"
                        />
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={isEmailLoading || isLoading || isDemoLoading}
                    className="w-full bg-white text-black hover:bg-zinc-200 font-semibold rounded-lg py-5"
                    data-testid="auth-submit-btn"
                  >
                    {isEmailLoading ? (
                      <div className="spinner border-black border-t-transparent" />
                    ) : (
                      mode === "signin" ? "Sign In" : "Create Account"
                    )}
                  </Button>
                </form>

                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-[#121212] lg:bg-[#18181B] px-3 text-zinc-400">or continue with</span>
                  </div>
                </div>

                <Button
                  data-testid="google-login-btn"
                  onClick={handleGoogleLogin}
                  disabled={isLoading || isDemoLoading || isEmailLoading}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10 font-semibold rounded-lg py-5"
                >
                  {isLoading ? (
                    <div className="spinner border-white border-t-transparent" />
                  ) : (
                    <>
                      <FcGoogle className="w-5 h-5 mr-3" />
                      Continue with Google
                    </>
                  )}
                </Button>

                {/* Toggle Sign In / Sign Up */}
                <div className="mt-6 pt-6 border-t border-white/5 text-center">
                  <p className="text-zinc-400 text-sm">
                    {mode === "signin" ? "Don't have an account?" : "Already have an account?"}
                    <button
                      type="button"
                      onClick={toggleMode}
                      className="ml-2 text-violet-400 hover:text-violet-300 font-medium transition-colors"
                      data-testid="toggle-auth-mode-btn"
                    >
                      {mode === "signin" ? "Sign Up" : "Sign In"}
                    </button>
                  </p>
                </div>

                {/* See ROAS-AI in Action widget */}
                <a href="/pricing-demo" className="block mt-4">
                  <div className="p-4 rounded-2xl border-2 border-violet-500/40 bg-gradient-to-r from-violet-600/10 to-fuchsia-600/10 hover:from-violet-600/20 hover:to-fuchsia-600/20 hover:border-violet-500/60 transition-all cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-bold text-sm">See ROAS-AI in Action</p>
                        <p className="text-zinc-400 text-xs mt-0.5">Try the free live interactive demo or choose a plan</p>
                      </div>
                      <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-semibold px-4 py-2 rounded-xl">
                        Get Started
                      </div>
                    </div>
                  </div>
                </a>

                {/* Affiliate Program Widget */}
                <a href="/affiliates" className="block mt-3 group">
                  <div className="p-4 rounded-2xl border border-emerald-500/20 bg-gradient-to-r from-emerald-600/5 to-teal-600/5 hover:from-emerald-600/15 hover:to-teal-600/15 hover:border-emerald-500/40 transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <DollarSign className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold text-sm">Become an Affiliate</p>
                        <p className="text-zinc-400 text-xs">Earn 20% recurring commissions — up to $1,998/yr per referral</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-emerald-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </a>

                {/* Product Simulator Widget (standalone /sim product) */}
                <a href="/sim" data-testid="login-product-simulator-link" className="block mt-3 group">
                  <div className="p-4 rounded-2xl border border-cyan-500/20 bg-gradient-to-r from-cyan-600/5 to-sky-600/5 hover:from-cyan-600/15 hover:to-sky-600/15 hover:border-cyan-500/40 transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                        <Radar className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold text-sm">Product Simulator</p>
                        <p className="text-zinc-400 text-xs">Test any website or mobile app on 10 synthetic users in 75 seconds</p>
                      </div>
                      <ArrowUpRight className="w-4 h-4 text-cyan-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </div>
                  </div>
                </a>
              </motion.div>
            </AnimatePresence>
          </div>

          <p className="text-zinc-500 text-sm text-center mt-6">
            By signing up, you agree to our{" "}
            <a href="/terms" className="text-violet-400 hover:text-violet-300">Terms of Service</a>
            {" "}and{" "}
            <a href="/privacy" className="text-violet-400 hover:text-violet-300">Privacy Policy</a>
            {" "} | {" "}
            <a href="/about" className="text-violet-400 hover:text-violet-300">About Us</a>
          </p>
        </motion.div>
      </div>
      <footer className="absolute bottom-4 right-4">
        <p className="text-zinc-500 text-xs">Copyright (c) 2026 Beruf LLC</p>
      </footer>
    </div>
  );
}
